import { FC } from 'react';
import { Checkbox, Col, Form, FormProps, Input, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import SpaceAtom from 'components/atoms/SpaceAtom';
import { Icon } from 'components/atoms/Icons';
import { streetTypesEnum } from 'constants/streetTypes';
import { streetTypesLiteralsManager } from 'utils/streetTypes';
import { ProjectListResponseDataModel } from 'core/domain/project/model/projectModel';

interface AssetEditFormProps extends FormProps {
  assetTypesList: string[];
  isBlocked?: boolean;
  onBlocked?: (isBlocked: boolean) => void;
  projects: ProjectListResponseDataModel[];
}

const AssetEditForm: FC<AssetEditFormProps> = ({
  assetTypesList,
  projects,
  isBlocked = false,
  onBlocked,
  ...props
}) => {
  const { t } = useTranslation();

  const streetTypes = Object.values(streetTypesEnum);

  return (
    <Form {...props}>
      <>
        <Row gutter={16}>
          <Col xs={24} sm={9}>
            <Form.Item name={'alias'}>
              <Input placeholder={t('deployment_alias')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={9}>
            <Form.Item name={'assetType'} rules={[{ required: true, message: t('field_required'), type: 'string' }]}>
              <Select placeholder={`${t('asset_type')}*`}>
                {assetTypesList.map((assetType, key) => (
                  <Select.Option key={key} value={assetType}>
                    {`${t(assetType)}`}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={6}>
            <Form.Item name={'isBlocked'}>
              <Checkbox
                checked={isBlocked}
                onChange={() => onBlocked?.(!isBlocked)}
              >
                {t('_BLOCKED')}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name={'streetType'} rules={[{ required: true, message: t('field_required'), type: 'string' }]}>
              <Select placeholder={`${t('street_type')}*`} allowClear>
                {streetTypes.map((streetType, key) => (
                  <Select.Option key={key} value={streetTypesLiteralsManager[streetType]()}>
                    {streetTypesLiteralsManager[streetType]()}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={11}>
            <Form.Item name={'streetName'} rules={[{ required: true, message: t('field_required') }]}>
              <Input placeholder={`${t('street_name')}*`} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={5}>
            <Form.Item name={'blockNumber'} rules={[{ required: true, message: t('field_required') }]}>
              <Input placeholder={`${t('block_number')}*`} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name={'staircase'}>
              <Input placeholder={t('stairway')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={'flat'}>
              <Input placeholder={t('storey')} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item name={'door'}>
              <Input placeholder={t('door')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <Form.Item name={'postalCode'} rules={[{ required: true, message: t('field_required') }]}>
              <Input placeholder={`${t('zip_code')}*`} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item name={'city'} rules={[{ required: true, message: t('field_required') }]}>
              <Input placeholder={`${t('city')}*`} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name={'province'} rules={[{ required: true, message: t('field_required') }]}>
              <Input placeholder={`${t('province')}*`} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={'country'} rules={[{ required: true, message: t('field_required') }]}>
              <Input placeholder={`${t('country')}*`} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name={'communityId'} rules={[{ required: true, message: t('field_required'), type: 'string' }]}>
              <Select placeholder={`${t('project')}*`} allowClear>
                {projects.map((project, key) => (
                  <Select.Option key={key} value={project.name}>
                    {project.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name={'externalId'}>
              <Input placeholder={t('_ASSET_DETAIL_EDIT_MODAL_EXTERNAL_ID')} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={24}>
            <Form.Item name={'description'}>
              <Input placeholder={t('description')} maxLength={100} />
            </Form.Item>
          </Col>
        </Row>

        <p>(*) {t('required_fields')}</p>
        <p>
          <SpaceAtom>
            <Icon.InfoCircle />
            <span>{t('asset_type_info')}</span>
          </SpaceAtom>
        </p>
      </>
    </Form>
  );
};

export default AssetEditForm;
